import { type KeyboardEvent } from "react";
import { ChevronIcon } from "vega-icons";

import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { Key } from "@vega-core/constants/global";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatFullName } from "@vega-core/utils/formatString";

import { type UserStateView } from "../../hooks/useCollaboratorRolesManager";

type Props = {
  collaborator: CollaboratorDetails;
  userState: UserStateView;
  hasFailed: boolean;
};

export const UserAssignmentSection = ({
  collaborator,
  userState,
  hasFailed,
}: Props) => {
  const { value: isExpanded, toggle: toggleExpanded } = useBoolean(true);

  const fullName = formatFullName({
    firstName: collaborator.firstName,
    middleName: collaborator.middleName,
    lastName: collaborator.lastName,
  });
  const initial = collaborator.firstName
    ? collaborator.firstName[0]
    : collaborator.email[0];

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter || event.key === Key.Space) {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div
      className={cnTwMerge(
        "flex flex-col shrink-0 rounded-containerCorner overflow-hidden border",
        hasFailed ? "border-border/negative" : "border-border/secondary",
      )}
      data-testid={`manage-roles-user-section-${collaborator.id}`}
    >
      <div
        aria-expanded={isExpanded}
        className="flex items-center justify-between gap-3 w-full text-left bg-text/secondary/10 px-4 py-3 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default"
        data-testid={`manage-roles-user-toggle-${collaborator.id}`}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Avatar initials={initial} />
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="capitalize body-base-medium text-text/primary truncate w-full">
              {fullName}
            </span>
            <span className="lowercase body-small-regular text-text/secondary truncate w-full">
              {collaborator.email}
            </span>
          </div>
        </div>
        <ChevronIcon
          className={cnTwMerge(
            "w-4 h-4 text-text/secondary shrink-0 transition-transform",
            isExpanded && "rotate-180",
          )}
        />
      </div>
      {isExpanded && userState.permissionsData.length > 0 ? (
        <div className="px-4 py-4">
          <CoreTable
            columns={userState.columns}
            data={userState.permissionsData}
            dataTestId={`manage-roles-table-${collaborator.id}`}
            noSorting
          />
        </div>
      ) : null}
    </div>
  );
};
